<!-- Карточка рейса в блоке исполнения заказа -->
<template>
  <div class="order-execution-card">
    <div class="order-execution-card__body">
      <div class="order-execution-card__trip-section">
        <div class="order-execution-card__header">
          <button class="order-execution-card__title-button" type="button" @click="$emit('open')">
            <span class="order-execution-card__title">
              {{ title }}
            </span>
          </button>
        </div>

        <div class="order-execution-card__details">
          <div class="order-execution-card__column">
            <span class="order-execution-card__label">Статус</span>
            <BaseStatusSelect
              compact
              :value="currentStatus"
              :label="tripStore.formatTripStatus(currentStatus)"
              :color="tripStore.tripStatusColor(currentStatus)"
              :options="tripStore.tripStatusSelectOptions"
              @change="changeTripStatus"
            />
          </div>

          <div class="order-execution-card__column">
            <span class="order-execution-card__label">ТС</span>
            <div class="order-execution-card__items">
              <VehicleEntityLink
                v-for="vehicle in vehicles"
                :key="vehicle.id || vehicle.registration_number"
                :vehicle="vehicle"
                class="order-execution-card__participant"
              />
              <span v-if="vehicles.length === 0" class="order-execution-card__muted">
                Не указано
              </span>
            </div>
          </div>

          <div class="order-execution-card__column">
            <span class="order-execution-card__label">Водители</span>
            <div class="order-execution-card__items">
              <DriverEntityLink
                v-for="driver in drivers"
                :key="driver.id || driver.full_name"
                :driver="driver"
                show-subtitle
                class="order-execution-card__participant"
              />
              <span v-if="drivers.length === 0" class="order-execution-card__muted">
                Не указано
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="order-execution-card__route-section">
        <TripRoutePreviewCard
          :trip-id="trip.id"
          :company-id="companyId"
          vertical
          @open="openRouteModal"
        />
      </div>
    </div>

    <BaseModal
      :visible="routeModalVisible"
      :title="routeModalTitle"
      size="wide"
      adaptive-fullscreen
      @close="closeRouteModal"
    >
      <TripRouteDetails
        v-if="routeModalVisible"
        :trip-id="trip.id"
        :company-id="companyId"
      />
    </BaseModal>
  </div>
</template>

<script>
import DriverEntityLink from "@/components/Driver/DriverEntityLink.vue";
import TripRouteDetails from "@/components/Trip/TripRouteDetails.vue";
import TripRoutePreviewCard from "@/components/Trip/TripRoutePreviewCard.vue";
import VehicleEntityLink from "@/components/Vehicle/VehicleEntityLink.vue";
import { useTripStore } from "@/stores/tripStore";

export default {
  name: "OrderExecutionCard",
  components: {
    DriverEntityLink,
    TripRouteDetails,
    TripRoutePreviewCard,
    VehicleEntityLink,
  },
  props: {
    trip: {
      type: Object,
      required: true,
    },
    companyId: {
      type: [Number, String],
      required: true,
    },
  },
  emits: ["open"],
  data() {
    return {
      currentStatus: this.trip.status,
      routeModalVisible: false,
    };
  },
  watch: {
    "trip.status"(status) {
      this.currentStatus = status;
    },
  },
  computed: {
    tripStore() {
      return useTripStore();
    },

    title() {
      return this.trip.name || `Рейс #${this.trip.id}`;
    },

    vehicles() {
      return Array.isArray(this.trip.vehicles) ? this.trip.vehicles : [];
    },

    drivers() {
      return Array.isArray(this.trip.drivers) ? this.trip.drivers : [];
    },

    routeModalTitle() {
      return `Маршрут: ${this.title}`;
    },
  },
  methods: {
    async changeTripStatus(status) {
      await this.tripStore.updateStatus(this.trip.id, status);
      this.currentStatus = status;
    },

    openRouteModal() {
      this.routeModalVisible = true;
    },

    closeRouteModal() {
      this.routeModalVisible = false;
    },
  },
};
</script>

<style scoped>
.order-execution-card {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 12px;
  color: inherit;
  text-align: left;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.order-execution-card:hover {
  border-color: #d1d5db;
  box-shadow: 0 1px 5px rgba(15, 23, 42, 0.08);
}

.order-execution-card__header {
  width: 100%;
}

.order-execution-card__title-button {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
  min-width: 0;
  padding: 0;
  color: inherit;
  text-align: left;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.order-execution-card__title {
  min-width: 0;
  overflow: hidden;
  color: var(--color-dark);
  font-size: 14px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.order-execution-card__label {
  color: #666;
  font-size: 12px;
}

.order-execution-card__body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(340px, 390px);
  gap: 14px;
  align-items: stretch;
  min-width: 0;
}

.order-execution-card__trip-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

.order-execution-card__route-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  padding-left: 18px;
  border-left: 1px solid #edf0f3;
}

.order-execution-card__section-label {
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
  text-transform: uppercase;
}

.order-execution-card__details {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  align-content: start;
  min-width: 0;
}

.order-execution-card__column {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
}

.order-execution-card__items {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 0;
}

.order-execution-card__participant {
  justify-self: start;
  min-width: 0;
  width: fit-content;
}

.order-execution-card__muted {
  color: #999;
  font-size: 12px;
}

@media (max-width: 620px) {
  .order-execution-card__body {
    grid-template-columns: 1fr;
  }

  .order-execution-card__route-section {
    padding-top: 12px;
    padding-left: 0;
    border-top: 1px solid #edf0f3;
    border-left: 0;
  }

  .order-execution-card__details {
    display: grid;
    grid-template-columns: 1fr;
  }
}
</style>
